'use client';

import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getHeroHeaderSizeClasses } from '@/utils/getHeroHeaderSizeClasses';
import { getChildHeadingVariant } from '@/utils/headingLevel';
import { getMainHeroVerticalSpacing } from '@/utils/getVerticalSpacing';
import { isVideoUrl } from '@/utils/isVideoUrl';
import { normalizeSvgEmbedMarkup } from '@/utils/normalizeSvgEmbed';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';
import VideoEmbed from './atoms/VideoEmbed';

function valOf(x, fallback = '') {
    if (x == null) return fallback;
    if (typeof x === 'object' && x && 'value' in x) return x.value ?? fallback;
    return x;
}

function MotionMaybe({ enable, k, motionProps, className, children }) {
    const [animationReady, setAnimationReady] = useState(false);

    useEffect(() => {
        setAnimationReady(true);
    }, []);

    if (enable) {
        if (!animationReady) {
            return (
                <motion.div
                    key={`${k}-prerender`}
                    className={className}
                    {...motionProps}
                    initial={false}
                    animate={undefined}
                    whileInView={undefined}
                >
                    {children}
                </motion.div>
            );
        }
        return (
            <motion.div key={`${k}-on`} className={className} {...motionProps}>
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ opacity: '', transform: '' }}
        >
            {children}
        </div>
    );
}

function getElementVariants(preset, duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    if (preset === 'slide') {
        return {
            offscreen: { opacity: 0, y: 16 },
            onscreen: {
                opacity: 1,
                y: 0,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    if (preset === 'zoom') {
        return {
            offscreen: { opacity: 0, scale: 0.96 },
            onscreen: {
                opacity: 1,
                scale: 1,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    return {
        offscreen: { opacity: 0 },
        onscreen: { opacity: 1, transition: { type: 'tween', duration: d } },
    };
}

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'fade',
        none: 'none',
        fade: 'fade',
        'fade-in': 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide-up': 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom-in': 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'fade';
}

function mapGroup(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (
        ['cta', 'ctas', 'ctas-only', 'buttons', 'button', 'cta-only'].includes(
            v
        )
    )
        return 'ctas';
    if (['media', 'medias', 'media-only', 'image', 'images'].includes(v))
        return 'medias';
    return 'content';
}

function getStaggerContainer(duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    return {
        hidden: { opacity: 1 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: Math.min(0.25, d * 0.35),
                delayChildren: Math.min(0.3, d * 0.4),
            },
        },
    };
}

function normalizeMediaUrl(input) {
    if (!input) return '';
    if (typeof input === 'string') return input.trim();
    if (typeof input === 'object') {
        const candidate =
            input.url ||
            input.src ||
            input.ImageUrl ||
            input.imageUrl ||
            input.value ||
            '';
        return typeof candidate === 'string' ? candidate.trim() : '';
    }
    return '';
}

function getBackgroundTransitionProps(rawTransition, prefersReducedMotion) {
    if (prefersReducedMotion) {
        return {
            initial: { opacity: 1 },
            animate: { opacity: 1 },
            exit: { opacity: 1 },
            transition: { duration: 0 },
        };
    }

    const transition = String(rawTransition || 'fade')
        .toLowerCase()
        .trim();
    const timing = { duration: 0.8, ease: 'easeInOut' };
    const transitions = {
        'slide left': {
            initial: { x: '100%', opacity: 1 },
            animate: { x: 0, opacity: 1 },
            exit: { x: '-100%', opacity: 1 },
        },
        'slide right': {
            initial: { x: '-100%', opacity: 1 },
            animate: { x: 0, opacity: 1 },
            exit: { x: '100%', opacity: 1 },
        },
        'slide up': {
            initial: { y: '100%', opacity: 1 },
            animate: { y: 0, opacity: 1 },
            exit: { y: '-100%', opacity: 1 },
        },
        'slide down': {
            initial: { y: '-100%', opacity: 1 },
            animate: { y: 0, opacity: 1 },
            exit: { y: '100%', opacity: 1 },
        },
        zoom: {
            initial: { opacity: 0, scale: 1.08 },
            animate: { opacity: 1, scale: 1 },
            exit: { opacity: 0, scale: 1 },
        },
        'zoom out': {
            initial: { opacity: 0, scale: 0.92 },
            animate: { opacity: 1, scale: 1 },
            exit: { opacity: 0, scale: 1 },
        },
        blur: {
            initial: { opacity: 0, filter: 'blur(18px)' },
            animate: { opacity: 1, filter: 'blur(0px)' },
            exit: { opacity: 0, filter: 'blur(0px)' },
        },
        wipe: {
            initial: { clipPath: 'inset(0 100% 0 0)' },
            animate: { clipPath: 'inset(0 0% 0 0)' },
            exit: { opacity: 1 },
        },
        'wipe up': {
            initial: { clipPath: 'inset(100% 0 0 0)' },
            animate: { clipPath: 'inset(0% 0 0 0)' },
            exit: { opacity: 1 },
        },
    };

    return {
        ...(transitions[transition] || {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            exit: { opacity: 0 },
        }),
        transition: timing,
    };
}

const isEmbedCode = (val) =>
    typeof val === 'string' &&
    /<\s*(iframe|script|video|svg|div|section|figure|blockquote)[\s>]/i.test(
        val
    );

export default function Hero(props) {
    const {
        Header,
        SubHeader,
        HeaderSize = 'large',
        Body,
        Alignment = 'right',
        ColorScheme = 'light',
        VerticalSpacing = 'medium',
        ImageVariant = 'square',
        ImageUrl,
        ImagePosition = '50',
        Images = [],
        SliderTransition = props.BackgroundTransition || 'Fade',
        BackgroundFixed = true,
        AltText,
    } = props;

    const normalizedImageUrl = valOf(ImageUrl, '');
    const normalizedVideoUrl = '';
    const videoIsEmbed = isEmbedCode(normalizedVideoUrl);
    const imageIsEmbed = isEmbedCode(normalizedImageUrl);
    const fallbackVideoUrl =
        !normalizedVideoUrl && isVideoUrl(normalizedImageUrl)
            ? normalizedImageUrl
            : '';
    const videoUrl = videoIsEmbed ? '' : normalizedVideoUrl || fallbackVideoUrl;
    const embedHtml = videoIsEmbed
        ? normalizedVideoUrl
        : imageIsEmbed
        ? normalizedImageUrl
        : '';
    const imageUrl = videoUrl || embedHtml ? '' : normalizedImageUrl;

    const {
        backgroundColor,
        taglineColor,
        textColor,
        thirdButton,
        secondaryButton,
        backgroundStyle,
        overlayColor,
        overlayStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const backgroundMedia = useMemo(
        () =>
            (Array.isArray(Images) ? Images : [])
                .map((image) => ({
                    url: normalizeMediaUrl(
                        image?.ImageUrl ?? image?.imageUrl ?? image
                    ),
                    position:
                        image?.ImagePosition ?? image?.imagePosition ?? '50',
                }))
                .filter((image) => image.url),
        [Images]
    );
    const backgroundVideoUrl =
        backgroundMedia.find((media) => isVideoUrl(media.url))?.url ||
        normalizeMediaUrl(props.Video);
    const backgroundImages = useMemo(
        () => backgroundMedia.filter((media) => !isVideoUrl(media.url)),
        [backgroundMedia]
    );
    const [activeBackgroundIndex, setActiveBackgroundIndex] = useState(0);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const activeBackground = backgroundImages[activeBackgroundIndex] || null;
    const hasBackgroundImages = backgroundImages.length > 0;

    useEffect(() => {
        setActiveBackgroundIndex((current) =>
            current < backgroundImages.length ? current : 0
        );
    }, [backgroundImages.length]);

    useEffect(() => {
        if (backgroundImages.length < 2) return;
        const interval = window.setInterval(() => {
            setActiveBackgroundIndex(
                (current) => (current + 1) % backgroundImages.length
            );
        }, 5000);
        return () => window.clearInterval(interval);
    }, [backgroundImages.length]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mediaQuery = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
        );
        const updatePreference = (event) =>
            setPrefersReducedMotion(event.matches);
        setPrefersReducedMotion(mediaQuery.matches);
        mediaQuery.addEventListener?.('change', updatePreference);
        return () =>
            mediaQuery.removeEventListener?.('change', updatePreference);
    }, []);

    const backgroundTransitionProps = getBackgroundTransitionProps(
        SliderTransition,
        prefersReducedMotion
    );

    const Animation = normalizeAnimation(valOf(props.Animation, 'fade'));
    const AnimationGroup = mapGroup(valOf(props.AnimationGroup, 'content'));
    const Duration = Number(valOf(props.Duration, 0.6)) || 0.6;
    const EditorPreview = Boolean(valOf(props.EditorPreview, false));

    const preset = String(Animation || 'fade').toLowerCase();
    const animateText = AnimationGroup === 'content';
    const animateMedia = AnimationGroup === 'medias';
    const animateCTAs = AnimationGroup === 'ctas';
    const enableAnim = preset !== 'none';

    const isStagger = preset === 'stagger';
    const viewport = { once: !EditorPreview, amount: 0.5 };
    const baseKey = `hero-${AnimationGroup}-${preset}-${Duration}-${
        EditorPreview ? 'preview' : 'prod'
    }`;

    const containerMotionProps =
        enableAnim && isStagger
            ? {
                  variants: getStaggerContainer(Duration),
                  initial: EditorPreview ? 'hidden' : 'hidden',
                  animate: EditorPreview ? 'show' : undefined,
                  whileInView: EditorPreview ? undefined : 'show',
                  viewport,
              }
            : {};
    const childMotionProps = enableAnim
        ? {
              variants: getElementVariants(preset, Duration),
              initial: 'offscreen',
              animate: EditorPreview ? 'onscreen' : undefined,
              whileInView: EditorPreview ? undefined : 'onscreen',
              viewport,
          }
        : {};

    let fontSize,
        subHeaderFontSize,
        textAlign,
        imageOrder,
        textOrder,
        gridTemplate,
        gap,
        buttonAlign,
        bodyAlign,
        imageOverflow;
    switch (Alignment) {
        case 'center':
            fontSize = getHeroHeaderSizeClasses(HeaderSize, 'center');
            subHeaderFontSize = getHeroHeaderSizeClasses('large', 'center');
            textAlign =
                'max-w-xl sm:max-w-2xl md:max-w-4xl lg:max-w-6xl xl:max-w-7xl text-center';
            bodyAlign = 'mx-auto';
            imageOrder = 'order-2';
            textOrder = 'order-1';
            gap = 'gap-y-4 lg:gap-y-12 gap-x-16';
            gridTemplate =
                'grid-cols-1 px-6 mx-auto md:max-w-4xl lg:max-w-5xl lg:justify-center lg:px-8 xl:max-w-6xl';
            buttonAlign = 'justify-center';
            break;
        case 'left':
            fontSize = getHeroHeaderSizeClasses(HeaderSize);
            subHeaderFontSize = getHeroHeaderSizeClasses('large');
            textAlign = 'max-w-xl sm:max-w-2xl text-center lg:text-left';
            imageOrder = 'order-2';
            textOrder = 'order-1';
            gap = 'gap-y-4 gap-x-16';
            gridTemplate =
                'lg:grid-cols-2 mx-auto px-6 md:max-w-4xl lg:max-w-7xl lg:justify-center lg:px-8 xl:max-w-[1440px]';
            buttonAlign = 'justify-center lg:justify-start';
            break;
        case 'right':
            fontSize = getHeroHeaderSizeClasses(HeaderSize);
            subHeaderFontSize = getHeroHeaderSizeClasses('large');
            textAlign = 'max-w-xl sm:max-w-2xl text-center lg:text-left';
            imageOrder = 'order-2 lg:order-1';
            textOrder = 'order-1 lg:order-2';
            gap = 'gap-y-4 gap-x-16';
            gridTemplate =
                'lg:grid-cols-2 px-6 mx-auto md:max-w-4xl lg:max-w-7xl lg:justify-center lg:px-8 xl:max-w-[1440px]';
            buttonAlign = 'justify-center lg:justify-start';
            break;
        case 'right-overflow':
            fontSize = getHeroHeaderSizeClasses(HeaderSize, 'overflow');
            subHeaderFontSize = getHeroHeaderSizeClasses('large', 'overflow');
            textAlign =
                'max-w-xl sm:max-w-2xl lg:max-w-4xl text-center lg:text-left';
            imageOrder = 'order-2 lg:order-2';
            imageOverflow = 'mx-auto lg:ml-auto';
            textOrder = 'order-1 lg:order-1';
            gap = 'gap-y-4 gap-x-16';
            gridTemplate =
                'lg:grid-cols-2 px-6 lg:px-8 lg:pr-0 lg:justify-center';
            buttonAlign = 'justify-center lg:justify-start';
            break;
        case 'left-overflow':
            fontSize = getHeroHeaderSizeClasses(HeaderSize, 'overflow');
            subHeaderFontSize = getHeroHeaderSizeClasses('large', 'overflow');
            textAlign =
                'max-w-xl sm:max-w-2xl lg:max-w-4xl text-center lg:text-left';
            imageOrder = 'order-1 lg:order-1';
            imageOverflow = 'lg:mr-auto mx-auto';
            textOrder = 'order-1 lg:order-2';
            gap = 'gap-y-4 gap-x-16';
            gridTemplate =
                'lg:grid-cols-2 px-6 lg:px-8 lg:pl-0 lg:justify-center';
            buttonAlign = 'justify-center lg:justify-start';
            break;
        default: // right
            fontSize = getHeroHeaderSizeClasses(HeaderSize);
            subHeaderFontSize = getHeroHeaderSizeClasses('large');
            textAlign = 'max-w-xl sm:max-w-2xl text-center lg:text-left';
            imageOrder = 'order-2 lg:order-1';
            textOrder = 'order-1 lg:order-2';
            gap = 'gap-y-4 gap-x-16';
            gridTemplate =
                'lg:grid-cols-2 px-6 mx-auto md:max-w-4xl lg:max-w-7xl lg:justify-center lg:px-8 xl:max-w-[1440px]';
            buttonAlign = 'justify-center lg:justify-start';
            break;
    }

    const { hasAny: hasCTAs, element: CTAs } = getCTA(props, {
        secondaryVariant: secondaryButton,
        outlineVariant: thirdButton,
        buttonAlign,
        wrapSpacing: 'mt-10 lg:mt-16',
        buttonsClassName: 'flex flex-wrap items-center gap-2',
        enableAnim: enableAnim && animateCTAs,
        motionProps: childMotionProps,
    });

    const normalizedVariant = String(ImageVariant || '').toLowerCase();
    const display =
        ['contain', 'landscape', 'portrait', 'square', 'cover'].find((opt) =>
            normalizedVariant.includes(opt)
        ) || 'cover';
    const hasShadow = !normalizedVariant.includes('no-shadow');
    const mediaAspectRatio =
        display === 'square'
            ? '1 / 1'
            : display === 'portrait'
            ? '3 / 4'
            : '16 / 9';

    let imageClass,
        shadow = hasShadow ? 'media-soft-shadow' : '',
        overFlow = 'overflow-hidden',
        imageRound = 'rounded-[var(--shape-radius)]',
        imageOverflowRound,
        imageEdgeClass = '',
        mediaMaxWidth = '100%';

    const adjustEdgeForOverflow =
        display === 'square' || display === 'portrait';
    if (adjustEdgeForOverflow && Alignment === 'right-overflow') {
        imageEdgeClass = 'lg:ml-auto';
    } else if (adjustEdgeForOverflow && Alignment === 'left-overflow') {
        imageEdgeClass = 'lg:mr-auto';
    }

    switch (display) {
        case 'square':
            imageClass =
                'object-cover h-[290px] w-[290px] md:h-[450px] md:w-[450px] xl:h-[550px] xl:w-[550px] max-w-[290px] md:max-w-[450px] xl:max-w-[550px]';
            mediaMaxWidth = '100%';
            break;
        case 'portrait':
            imageClass = 'object-cover w-full h-full';
            mediaMaxWidth = '600px';
            break;
        case 'landscape':
            imageClass = 'object-cover w-full h-full';
            mediaMaxWidth = '1248px';
            break;
        case 'contain':
            imageClass =
                'object-contain w-auto max-h-[300px] md:max-h-[500px] lg:max-h-[600px] max-w-full mx-auto';
            mediaMaxWidth = '100%';
            break;
        default: // cover
            imageClass =
                'object-cover md:h-[500px] lg:h-[600px] max-w-[290px] sm:max-w-[600px] md:max-w-[750px] lg:max-w-[1024px] xl:max-w-[1248px] w-full h-full';
            mediaMaxWidth = '100%';
            break;
    }

    const isSquare = display === 'square';
    const useWrapperAspect = display === 'portrait' || display === 'landscape';
    const { x: imagePosX, y: imagePosY } = parseImagePosition(ImagePosition);
    const imageObjectPosition = `${imagePosX}% ${imagePosY}%`;
    const normalizedEmbedHtml = embedHtml
        ? normalizeSvgEmbedMarkup(embedHtml, {
              x: imagePosX,
              y: imagePosY,
              mode: display === 'contain' ? 'contain' : 'cover',
          })
        : '';
    const imageStyle = {
        ...(isSquare ? {} : { maxWidth: mediaMaxWidth }),
        objectPosition: imageObjectPosition,
    };

    if (Alignment === 'right-overflow') {
        imageOverflowRound =
            'rounded-[var(--shape-radius)] lg:rounded-tr-none lg:rounded-br-none';
    } else if (Alignment === 'left-overflow') {
        imageOverflowRound =
            'rounded-[var(--shape-radius)] lg:rounded-tl-none lg:rounded-bl-none';
    } else {
        imageOverflowRound = '';
    }

    // Define vertical spacing (centralized helper; hero-specific no-bottom uses pb-0)
    const verticalRaw =
        VerticalSpacing &&
        typeof VerticalSpacing === 'object' &&
        'value' in VerticalSpacing
            ? VerticalSpacing.value
            : VerticalSpacing;
    const verticalKey = String(verticalRaw || 'medium').toLowerCase();
    const heroSpace = getMainHeroVerticalSpacing(verticalKey);

    const noBottomKeys = new Set(['no-bottom', 'none', 'large-no-bottom']);
    let vSpace = noBottomKeys.has(verticalKey)
        ? `${heroSpace.topClass} pb-0`
        : `${heroSpace.className}`;
    let imageShift;
    if (noBottomKeys.has(verticalKey) || verticalKey === 'no-top') {
        imageShift = 'md:mt-auto';
    }
    const textBottomPadding = noBottomKeys.has(verticalKey)
        ? ''
        : 'pb-12 sm:pb-20';
    // Apply top-only rounding when we drop bottom padding
    if (noBottomKeys.has(verticalKey)) {
        imageRound = 'rounded-t-[var(--shape-radius)]';
        if (Alignment === 'right-overflow') {
            imageOverflowRound =
                'rounded-t-[var(--shape-radius)] lg:rounded-tr-none lg:rounded-br-none';
        } else if (Alignment === 'left-overflow') {
            imageOverflowRound =
                'rounded-t-[var(--shape-radius)] lg:rounded-tl-none lg:rounded-bl-none';
        } else {
            imageOverflowRound = '';
        }
    }

    return (
        <div
            id="top"
            data-hero
            className={`editable-component relative ${overFlow} pt-10 ${backgroundColor}`}
            style={{
                ...backgroundStyle,
                marginTop: '-172px',
                '@media (minWidth: 768px)': { marginTop: '-70px' },
            }}
        >
            {(hasBackgroundImages || backgroundVideoUrl) && (
                <>
                    {backgroundVideoUrl ? (
                        <VideoEmbed
                            url={backgroundVideoUrl}
                            playing
                            muted
                            loop
                            controls={false}
                            objectFit="cover"
                            className="pointer-events-none absolute inset-0 h-full w-full"
                        />
                    ) : (
                        <AnimatePresence initial={false}>
                            <motion.div
                                key={`${activeBackgroundIndex}-${activeBackground.url}`}
                                className={`absolute inset-0 bg-cover bg-no-repeat ${
                                    BackgroundFixed === false ||
                                    BackgroundFixed === 'false' ||
                                    backgroundImages.length > 1
                                        ? 'bg-scroll'
                                        : 'bg-scroll lg:bg-fixed'
                                }`}
                                style={{
                                    backgroundImage: `url(${activeBackground.url})`,
                                    backgroundPosition: `${
                                        parseImagePosition(
                                            activeBackground.position
                                        ).x
                                    }% ${
                                        parseImagePosition(
                                            activeBackground.position
                                        ).y
                                    }%`,
                                }}
                                {...(backgroundImages.length > 1
                                    ? backgroundTransitionProps
                                    : {})}
                            />
                        </AnimatePresence>
                    )}
                    <div
                        className={`absolute inset-0 ${overlayColor || ''}`}
                        style={overlayStyle}
                    />
                </>
            )}
            <div
                className={`relative z-40 mx-auto grid ${gridTemplate} ${gap} ${vSpace}`}
            >
                <MotionMaybe
                    enable={enableAnim && animateText}
                    k={`${baseKey}-text`}
                    motionProps={
                        enableAnim && animateText && isStagger
                            ? containerMotionProps
                            : childMotionProps
                    }
                    className={`${textOrder} mx-auto flex items-center justify-center ${textBottomPadding} sm:pt-12 ${textAlign}`}
                >
                    <div className="my-auto">
                        {Header && (
                            <MarkDown
                                variant="h1"
                                className={`font-display tracking-tight ${fontSize} ${taglineColor}`}
                            >
                                {Header}
                            </MarkDown>
                        )}
                        {SubHeader && (
                            <MarkDown
                                variant={getChildHeadingVariant(Header, 1)}
                                className={`mt-2 font-display tracking-tight ${subHeaderFontSize} ${taglineColor}`}
                            >
                                {SubHeader}
                            </MarkDown>
                        )}
                        <MarkDown
                            className={`mt-6 max-w-3xl text-lg font-text leading-7 md:text-xl md:leading-8 ${bodyAlign} ${textColor}`}
                        >
                            {Body}
                        </MarkDown>
                        {hasCTAs && CTAs}
                    </div>
                </MotionMaybe>
                <MotionMaybe
                    enable={enableAnim && animateMedia}
                    k={`${baseKey}-media`}
                    motionProps={
                        enableAnim && animateMedia && isStagger
                            ? containerMotionProps
                            : childMotionProps
                    }
                    className={`${imageOrder} ${imageShift} flex items-center justify-center ${
                        videoUrl ? '' : imageEdgeClass
                    }`}
                >
                    {embedHtml ? (
                        <div
                            className={`relative ${shadow} ${imageRound} ${imageOverflowRound} ${imageOverflow} ${imageEdgeClass} overflow-hidden`}
                            style={{
                                aspectRatio: mediaAspectRatio,
                                maxWidth: mediaMaxWidth,
                                width: '100%',
                            }}
                        >
                            <div
                                className="absolute inset-0 [&>*]:h-full [&>*]:max-h-none [&>*]:w-full [&>*]:max-w-none [&>svg]:block"
                                dangerouslySetInnerHTML={{
                                    __html: normalizedEmbedHtml,
                                }}
                            />
                        </div>
                    ) : videoUrl ? (
                        <div
                            className={`relative ${imageClass} ${shadow} ${imageRound} ${imageOverflowRound} ${imageOverflow} ${imageEdgeClass} overflow-hidden bg-black`}
                            style={{
                                aspectRatio: mediaAspectRatio,
                                maxWidth: mediaMaxWidth,
                                width: '100%',
                            }}
                        >
                            <VideoEmbed
                                url={videoUrl}
                                controls
                                playing
                                muted
                                loop
                                width="100%"
                                height="100%"
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                }}
                            />
                        </div>
                    ) : (
                        <div
                            className={`relative ${shadow} ${imageRound} ${imageOverflowRound} ${imageOverflow} ${imageEdgeClass} overflow-hidden`}
                            style={
                                display === 'contain'
                                    ? {
                                          maxWidth: '100%',
                                          width: '100%',
                                      }
                                    : useWrapperAspect
                                    ? {
                                          aspectRatio: mediaAspectRatio,
                                          maxWidth: mediaMaxWidth,
                                          width: '100%',
                                      }
                                    : isSquare
                                    ? {
                                          width: 'fit-content',
                                      }
                                    : {
                                          maxWidth: mediaMaxWidth,
                                          width: '100%',
                                      }
                            }
                        >
                            <img
                                id="hero-image"
                                className={imageClass}
                                style={imageStyle}
                                width="800"
                                height="800"
                                src={imageUrl}
                                alt={AltText}
                            />
                        </div>
                    )}
                </MotionMaybe>
            </div>
        </div>
    );
}
